import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { blueDark, black, white } from '@/theme/colors'
import { shadows, radii, motion, layers } from '@/theme/tokens'
import { fontSizes, text } from '@/theme/typography'

/**
 * Below this width the sidebar stops being a column beside the content and
 * becomes a drawer over it. Matches the point at which the admin tables start
 * needing the full viewport width.
 */
export const SIDEBAR_BREAKPOINT = '900px'

export const SIDEBAR_WIDTH = '224px';

/**
 * Only mounted while the drawer is open — its presence is what makes "open"
 * observable, and clicking it closes. Never shown at desktop width, where the
 * sidebar is a permanent column and there is nothing to dismiss.
 */
export const Backdrop = styled.div`
	position: fixed;
	inset: 0;
	z-index: ${layers.scrim};
	background-color: ${black}66;

	@media (min-width: ${SIDEBAR_BREAKPOINT}) {
		display: none;
	}
`

export const Sidebar = styled.nav<{ $open: boolean }>`
	display: flex;
	flex-direction: column;
	flex-shrink: 0;
	width: ${SIDEBAR_WIDTH};
	height: 100%;
	padding: 20px 0;
	background-color: ${blueDark};
	color: ${white};
	overflow-y: auto;

	@media (max-width: ${SIDEBAR_BREAKPOINT}) {
		position: fixed;
		inset: 0 auto 0 0;
		z-index: ${layers.modal};
		transform: translateX(${({ $open }) => ($open ? '0' : '-100%')});
		transition: transform ${motion.base};
		box-shadow: ${({ $open }) => ($open ? shadows.overlay : 'none')};
	}
`

export const Brand = styled.span`
	font-weight: 700;
	font-size: ${fontSizes.title};
	color: ${white};
	padding: 0 20px;
	margin-bottom: 24px;
`

export const GroupLabel = styled.span`
	padding: 0 20px;
	margin: 16px 0 6px;
	${text.eyebrow}
	color: ${white}8c;
`

export const NavLink = styled(Link)<{ $active?: boolean }>`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px 20px;
	color: ${white};
	text-decoration: none;
	font-size: ${fontSizes.base};
	font-weight: ${({ $active }) => ($active ? 700 : 500)};
	opacity: ${({ $active }) => ($active ? 1 : 0.78)};
	border-left: 3px solid ${({ $active }) => ($active ? white : 'transparent')};

	svg {
		font-size: ${fontSizes.title};
	}

	&:hover {
		opacity: 1;
		background-color: ${white}14;
	}
`

/** Pushes "Back to app" to the bottom of the column. */
export const Spacer = styled.div`
	flex: 1;
	min-height: 16px;
`

export const BackLink = styled(NavLink)`
	border-left-color: transparent;
	font-size: ${fontSizes.small};
`

/**
 * The drawer toggle. Hidden at desktop width, where the sidebar is always on
 * screen and a control that opens it would be meaningless.
 */
export const MenuButton = styled.button`
	display: none;
	align-items: center;
	justify-content: center;
	width: 38px;
	height: 38px;
	flex-shrink: 0;
	border: 1px solid ${white}66;
	border-radius: ${radii.small};
	background-color: ${white}1f;
	color: ${white};
	cursor: pointer;

	svg {
		font-size: ${fontSizes.heading};
	}

	&:hover {
		border-color: ${white};
	}

	@media (max-width: ${SIDEBAR_BREAKPOINT}) {
		display: inline-flex;
	}
`
